import { Dispatch, SetStateAction, use, useEffect, useState } from 'react';
import { FieldValues, useForm, UseFormReturn } from 'react-hook-form';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { useQueryClient } from '@tanstack/react-query';
import ConfirmationPopupProviderContext from 'shared/components/confirmation-pop-up/providers/contexts/confirmation-popup-provider-context';
import ErrorMessageProviderContext from 'shared/components/error/providers/contexts/error-message-context';
import ViewingMode from 'shared/components/viewing-mode-controller/types/viewing-mode';
import AuthProviderContext from 'shared/features/auth/authentication/providers/contexts/auth-provider-context';
import { useLogging } from 'shared/logging/providers/logging.provider';
import { clearAllActiveFilters } from 'shared/utils/clear-filters';

import { ReadTenantsResponse } from 'features/tenants/models/read-tenants-response';
import { Tenant } from 'features/tenants/models/tenant';
import { TenantAccess } from 'features/tenants/models/tenant-access';
import { UpdateTenantCommand } from 'features/tenants/models/update-tenant-command';
import TenantAccessIndicatorProviderContext from 'features/tenants/providers/tenant-access-indicator-provider/context/tenant-access-indicator-provider-context';
import { useLazyAccessTenant, useReadTenants, useUpdateTenant } from 'features/tenants/providers/tenants-provider/tenant-queries';

export type TenantsProviderState = {
  viewingMode: ViewingMode;
  readTenantsData: ReadTenantsResponse | undefined;
  readTenantsIsSuccess: boolean;
  readTenantsIsLoading: boolean;
  isTenantPopupOpen: boolean;
  currentTenant: Tenant | null;
  isUpdatingTenant: boolean;
  form: UseFormReturn<Tenant>;
  setViewingMode: Dispatch<SetStateAction<ViewingMode>>;
  openDetails: (viewingMode: ViewingMode, tenant: Tenant) => void;
  closeDetails: () => void;
  cancelEditDetails: () => void;
  submitTenant: (fieldValues: FieldValues) => void;
  onAccessTenant: (tenant: Tenant) => void;
  accessTenantData: TenantAccess | undefined;
  accessTenantIsLoading: boolean;
  allowOnAccessTenant: (tenant?: Tenant) => boolean;
};

const useTenantsProviderState = (): TenantsProviderState => {
  const { setErrorMessage } = use(ErrorMessageProviderContext);
  const { temporaryTenantAccess } = use(TenantAccessIndicatorProviderContext);
  const { showDefaultConfirmationPopUp } = use(ConfirmationPopupProviderContext);
  const queryClient = useQueryClient();

  const { log } = useLogging();
  const {
    tenant: { name: tenantName },
  } = use(AuthProviderContext);

  const [viewingMode, setViewingMode] = useState<ViewingMode>('none');
  const [isTenantPopupOpen, setIsTenantPopupOpen] = useState<boolean>(false);
  const [currentTenant, setCurrentTenant] = useState<Tenant | null>(null);
  const [isUpdatingTenant, setIsUpdatingTenant] = useState<boolean>(false);

  const form = useForm({
    defaultValues: {
      realmName: '',
      note: '',
    } as Tenant,
    mode: 'onBlur',
  });

  const {
    formState: { isDirty },
    reset,
  } = form;

  const {
    data: readTenantsData,
    isSuccess: readTenantsIsSuccess,
    isLoading: readTenantsIsLoading,
    error: readTenantsError,
  } = useReadTenants();

  const [accessTenantName, setAccessTenantName] = useState<string | null>(null);
  const {
    refetch: getAccessTenant,
    data: accessTenantData,
    isLoading: accessTenantIsLoading,
    error: accessTenantError,
  } = useLazyAccessTenant(accessTenantName ?? '');

  useEffect(() => {
    if (readTenantsError || accessTenantError) {
      const error = readTenantsError ?? accessTenantError;
      if (error) {
        setErrorMessage(error);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readTenantsError, accessTenantError]);

  useEffect(() => {
    if (currentTenant && accessTenantData) {
      temporaryTenantAccess(accessTenantData, currentTenant);
    }
  }, [currentTenant, accessTenantData]);

  const onAccessTenant = (tenant: Tenant): void => {
    log(`switching to tenant: ${tenant.realmName}`, SeverityLevel.Information);
    clearAllActiveFilters();
    queryClient.clear();
    setAccessTenantName(tenant.realmName);
  };

  useEffect(() => {
    if (accessTenantName) getAccessTenant();
  }, [accessTenantName]);

  const allowOnAccessTenant = (tenant?: Tenant): boolean => {
    if (tenant) {
      return tenantName?.toLowerCase() !== tenant.realmName.toLowerCase();
    } else {
      return tenantName?.toLowerCase() !== currentTenant?.realmName.toLowerCase();
    }
  };

  const { mutate: updateTenant, error: updateTenantError, isError: updateTenantIsError, isPending: isLoading } = useUpdateTenant();

  const openDetails = (viewingMode: ViewingMode, tenant: Tenant): void => {
    reset(tenant);
    setViewingMode(viewingMode);
    setCurrentTenant(tenant);
    setIsTenantPopupOpen(true);
  };

  const cancelEditDetails = (): void => {
    setViewingMode('viewing');
    reset();
  };

  const closeDetails = (): void => {
    showDefaultConfirmationPopUp({
      shouldShow: viewingMode === 'editing' && isDirty,
      action: () => {
        setIsTenantPopupOpen(false);
        setViewingMode('viewing');
      },
    });
  };

  const submitTenant = (fieldValues: FieldValues): void => {
    setIsUpdatingTenant(true);

    const tenant: UpdateTenantCommand = {
      realmName: fieldValues.realmName?.trim(),
      note: fieldValues.note?.trim(),
    };

    updateTenant(tenant);
    setViewingMode('viewing');
  };

  useEffect(() => {
    if (readTenantsData) {
      const tenant = readTenantsData.tenants.find((t: Tenant) => t.realmName === currentTenant?.realmName);

      if (tenant) {
        setCurrentTenant(tenant);
        reset(tenant);
      }
    }

    setIsUpdatingTenant(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readTenantsData, isLoading]);

  useEffect(() => {
    if (updateTenantIsError) {
      setErrorMessage(updateTenantError);
    }
    if (currentTenant) {
      reset(currentTenant);
    }
    setIsUpdatingTenant(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateTenantIsError]);

  return {
    viewingMode,
    readTenantsData,
    readTenantsIsSuccess,
    readTenantsIsLoading,
    isTenantPopupOpen,
    currentTenant,
    isUpdatingTenant,
    form,
    setViewingMode,
    openDetails,
    closeDetails,
    cancelEditDetails,
    submitTenant,
    onAccessTenant,
    accessTenantData,
    accessTenantIsLoading,
    allowOnAccessTenant,
  };
};

export default useTenantsProviderState;
